import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../../Components/Header/Header';
import ConfirmationBox from '../../Components/Confirmation/ConfirmationBox';
import api from '../../api';
import {
  STEPS, STEP_INDEX, copyText, downloadBlob, errorMessage, isBusy, kb, loadDocChannels, loadDocOptions, mediaUrl,
} from './docShared';
import BrandKitEditor, { cleanKit, kitProblem } from './BrandKitEditor';
import './documentaries.css';

function Stepper({ step }) {
  const at = STEP_INDEX[step] ?? 0;
  const failed = step === 'voice_failed' || step === 'render_failed';
  return (
    <ol className="docs-stepper">
      {STEPS.map((s, i) => {
        const state = i < at || step === 'done' ? 'done' : i === at ? (failed ? 'bad' : 'now') : 'later';
        return (
          <li key={s.key} className={`docs-step docs-step--${state}`}>
            <span className="docs-step__dot">{state === 'done' ? '✓' : state === 'bad' ? '!' : i + 1}</span>
            <span className="docs-step__label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** A video that is not busy can go back (owner, 2026-10-22): the voice and its word timings stay as they are;
 *  the brand kit and the plan can change, then render again — on this same video, as its next version. */
function GoBackSteps({ video, open, setOpen }) {
  const done = video.step === 'done';
  const item = (key, num, label, sub, onClick, locked = false) => (
    <li key={key} className={`docs-goback__item ${open === key ? 'is-open' : ''} ${locked ? 'is-locked' : ''}`}>
      {locked ? (
        <div className="docs-goback__btn" aria-disabled="true">
          <span className="docs-goback__num" aria-hidden="true">🔒</span>
          <span><strong>{label}</strong><small>{sub}</small></span>
        </div>
      ) : (
        <button type="button" className="docs-goback__btn" aria-pressed={open === key} onClick={onClick}>
          <span className="docs-goback__num">{num}</span>
          <span><strong>{label}</strong><small>{sub}</small></span>
        </button>
      )}
    </li>
  );
  return (
    <nav className="docs-goback" aria-label="Production steps">
      <ol>
        {item('voice', 1, 'Voice & timings', 'Fixed — everything is timed to it', null, true)}
        {item('brand', 2, 'Brand kit', 'Look, colours, motion', () => setOpen(open === 'brand' ? null : 'brand'))}
        {item('plan', 3, 'Plan', 'Paste a changed plan', () => setOpen(open === 'plan' ? null : 'plan'))}
        {item('render', 4, done ? 'Render again' : 'Render', done ? 'Makes the next version' : 'Make the video',
          () => setOpen('plan'))}
      </ol>
    </nav>
  );
}

const BRAND_EDITABLE = ['look', 'accent', 'background', 'card', 'panel', 'genre', 'visual_mode', 'notes', 'motion', 'avatar'];

/** This video's brand kit, changed after the voice was made (also when the video is finished). Only this video
 *  changes — unless "Also save to the channel" is ticked. The plan is checked again with the new kit. */
function ChangeBrand({ video, onSaved, onClose }) {
  const [options, setOptions] = useState(null);
  const [channel, setChannel] = useState(null);
  const [kit, setKit] = useState(() => {
    const b = video.request?.brand || {};
    const k = {};
    BRAND_EDITABLE.forEach((f) => { if (b[f] !== undefined && b[f] !== null && b[f] !== '') k[f] = b[f]; });
    return k;
  });
  const [toChannel, setToChannel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    // the looks come from a copy loaded once (and fetched ahead when the project page opens): the studio shows at
    // once; the channel (only needed for "Also save it to the channel") arrives on its own
    let live = true;
    loadDocOptions()
      .then((o) => { if (live) setOptions(o); })
      .catch((e) => live && setErr(errorMessage(e, 'Could not load the brand kit options.')));
    const cid = video.request?.channel_id;
    loadDocChannels()
      .then((list) => { if (live) setChannel((list || []).find((x) => x.id === cid) || null); })
      .catch(() => {});
    return () => { live = false; };
  }, [video.request?.channel_id]);
  const look = options?.look_details?.[kit.look] || null;
  const problem = options ? kitProblem(kit, look) : '';
  const save = () => {
    if (problem) return;
    setSaving(true);
    setErr('');
    const clean = cleanKit(kit);
    delete clean.voice;
    delete clean.voice_name; // the voice is the video's own: it never changes here
    api.put(`/visual-engine/videos/${video.id}/brand`, { ...clean, brand_notes: clean.notes || '', save_to_channel: toChannel })
      .then((r) => {
        if (r.data.saved_to_channel) loadDocChannels(true).catch(() => {});
        toast.success(r.data.saved_to_channel
          ? `Brand kit changed for this video and saved to ${channel?.name || 'the channel'}.`
          : 'Brand kit changed for this video.');
        onSaved(r.data.video);
      })
      .catch((e) => setErr(errorMessage(e, 'The brand kit could not be changed.')))
      .finally(() => setSaving(false));
  };
  return (
    <section className="docs-card docs-brandchange">
      <h2>Brand kit for this video</h2>
      <p className="docs-hint">
        Same voice and timings. Change the look, colours or motion; your plan is checked again with them, then
        render again — the new version is added to this video, the earlier renders stay.
      </p>
      {!options && !err && <div className="docs-empty">Loading…</div>}
      {options && (
        <BrandKitEditor options={options} kit={kit} onChange={setKit} channel={channel} title={video.title}
                        showSave={false} />
      )}
      {err && <div className="docs-alert docs-alert--bad">{err}</div>}
      <div className="docs-brandchange__bar">
        {channel && (
          <label className="docs-check">
            <input type="checkbox" checked={toChannel} onChange={(e) => setToChannel(e.target.checked)} />
            Also save it as {channel.name}&apos;s brand kit
          </label>
        )}
        <span className="docs-brandchange__problem">{problem}</span>
        <button type="button" className="docs-btn docs-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="docs-btn docs-btn--primary" onClick={save}
                disabled={!options || saving || Boolean(problem)}>
          {saving ? 'Saving…' : 'Use this brand kit'}
        </button>
      </div>
    </section>
  );
}

function Progress({ video, label }) {
  const pct = Math.round((video.progress || 0) * 100);
  return (
    <div className="docs-card docs-progress">
      <div className="docs-progress__top">
        <span className="docs-spinner" aria-hidden="true" />
        <div>
          <h2>{label}</h2>
          <p>
            {video.status === 'queued'
              ? `Waiting in line${video.queue_position ? ` — #${video.queue_position}` : ''}`
              : `${video.stage || 'Working'}…`}
          </p>
        </div>
        <span className="docs-progress__pct">{video.status === 'queued' ? '' : `${pct}%`}</span>
      </div>
      <div className="docs-bar"><div style={{ width: `${video.status === 'queued' ? 2 : pct}%` }} /></div>
    </div>
  );
}

function CopyForClaude({ id, title }) {
  const [pkg, setPkg] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    api.get(`/visual-engine/videos/${id}/package`)
      .then((r) => setPkg(r.data))
      .catch((e) => setError(errorMessage(e, 'Could not load the files.')));
  }, [id]);

  const copy = async (key, text) => {
    if (await copyText(text)) {
      setCopied(key);
      setTimeout(() => setCopied(''), 1800);
    } else {
      toast.error('Copy failed — use "Download zip" instead.');
    }
  };

  if (error) return <div className="docs-alert docs-alert--bad">{error}</div>;
  if (!pkg) return <div className="docs-muted">Loading files…</div>;
  return (
    <div className="docs-copy">
      <div className="docs-copy__main">
        <button className="docs-btn docs-btn--primary docs-btn--big" onClick={() => copy('all', pkg.copy_all)}>
          {copied === 'all' ? '✓ Copied — now paste into Claude' : 'Copy everything for Claude'}
        </button>
        <button className="docs-btn" onClick={() => downloadBlob(`/visual-engine/videos/${id}/package.zip`,
          `${title || 'video'} - for Claude.zip`).catch((e) => toast.error(errorMessage(e)))}>
          Download zip
        </button>
      </div>
      <details className="docs-files">
        <summary>Or copy the files one by one ({pkg.files.length})</summary>
        <ul>
          {pkg.files.map((f) => (
            <li key={f.name}>
              <span><strong>{f.title}</strong> <code>{f.name}</code> <em>{kb(f.content.length)}</em></span>
              <button className="docs-btn docs-btn--small" onClick={() => copy(f.name, f.content)}>
                {copied === f.name ? '✓ Copied' : 'Copy'}
              </button>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

/** "Fix these first": the errors that lock the render, each with its scene, time and fix, plus one paste for
 *  Claude holding only the scenes that need fixing. Warnings never block. */
function FixList({ id, plan }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      const r = await api.get(`/visual-engine/videos/${id}/fixes`);
      if (await copyText(r.data.text || '')) { setCopied(true); setTimeout(() => setCopied(false), 1800); }
      else toast.error('Copy failed.');
    } catch (e) { toast.error(errorMessage(e, 'Could not build the paste.')); }
  };
  const items = plan.fix_list || [];
  return (
    <div className="docs-fixes">
      <div className="docs-fixes__head">
        <strong>✗ Fix these first — the render is locked until every item is fixed</strong>
        <button className="docs-btn docs-btn--small" onClick={copy}>{copied ? '✓ Copied' : 'Copy for Claude'}</button>
      </div>
      <p className="docs-hint">Paste it into the same Claude chat. Claude replies with only the changed scenes —
        put them in the box “Paste fixed scenes” below.</p>
      <ol className="docs-fixes__list">
        {items.length > 0 ? items.map((f, i) => (
          <li key={i}>
            <span className="docs-fixes__where">{f.scene ? `${f.scene}${f.clock ? ` · ${f.clock}` : ''}` : 'plan'}</span>
            <span className="docs-fixes__what">{f.what}{f.fix ? <em> → {f.fix}</em> : null}</span>
            {f.why && <span className="docs-fixes__why">{f.why}</span>}
          </li>
        )) : (plan.blocking || []).map((p, i) => <li key={i}><span className="docs-fixes__what">{p}</span></li>)}
      </ol>
    </div>
  );
}

function PlanResult({ id, plan }) {
  if (!plan) return null;
  const deep = plan.deep || {};
  const checking = deep.status === 'checking';
  return (
    <div className={`docs-result ${plan.ok ? 'docs-result--good' : checking ? 'docs-result--wait' : 'docs-result--bad'}`}>
      {plan.ok && (
        <strong>✓ Plan passed — {plan.scenes} scenes (version {plan.version})
          {deep.pictures === 'skipped' ? ' · pictures not checked' : ''}</strong>
      )}
      {!plan.ok && checking && (
        <strong><span className="docs-spinner docs-spinner--small" aria-hidden="true" />{' '}
          {deep.phase === 'rules' || !deep.phase
            ? 'Checking the plan against the timings and the rules…'
            : `Checking the pictures${deep.total ? ` — ${deep.done || 0} / ${deep.total} scenes` : '…'}`}
          {' '}This page updates by itself.</strong>
      )}
      {checking && deep.total > 0 && (
        <div className="docs-bar docs-bar--thin"><div style={{ width: `${Math.round(100 * (deep.done || 0) / deep.total)}%` }} /></div>
      )}
      {!plan.ok && !checking && <FixList id={id} plan={plan} />}
      {plan.render_refused && <div className="docs-alert docs-alert--bad">The render did not start: {plan.render_refused}</div>}
    </div>
  );
}

/** The colours this video was made with (stored when it was created): look, accent and the three backgrounds.
 *  "the look's own" = nothing was set in the brand kit then — a brand kit saved later does not change this video. */
function VideoColours({ kit }) {
  const sw = (label, col, fallback) => (
    <span className="docs-colour" key={label}>
      <span className="docs-colour__sw" style={{ background: col || 'transparent' }} data-empty={col ? undefined : 'yes'} />
      <span><strong>{label}</strong> {col || fallback}</span>
    </span>
  );
  return (
    <div className="docs-colours" aria-label="This video's colours">
      {sw('Accent', kit.accent, "the look's own")}
      {sw('Background', kit.background, "the look's own")}
      {sw('Text cards', kit.card, 'same as background')}
      {sw('Text over pictures', kit.panel, "the look's own")}
      {!kit.background && (
        <span className="docs-colours__note">No brand background is set for this video, so it uses the look&apos;s
          own colours. Open <strong>Brand kit</strong> below to choose yours, then render again.</span>
      )}
    </div>
  );
}

/** The optional picture check (faces, broken links): off unless switched on for this video. Off drops the
 *  current plan's picture errors at once; on checks the current plan's pictures now. */
function PictureCheck({ video, onSaved }) {
  const [busy, setBusy] = useState(false);
  const on = video.check_pictures === true;
  const flip = () => {
    setBusy(true);
    api.post(`/visual-engine/videos/${video.id}/check-pictures`, { on: !on })
      .then((r) => { onSaved(r.data.video); toast.success(on ? 'Picture check off — only the plan rules are checked.'
        : 'Picture check on — checking the pictures now.'); })
      .catch((e) => toast.error(errorMessage(e)))
      .finally(() => setBusy(false));
  };
  return (
    <label className="bk-switch docs-piccheck">
      <input type="checkbox" checked={on} disabled={busy} onChange={flip} />
      <span className="bk-switch__track" aria-hidden="true"><span className="bk-switch__dot" /></span>
      <span className="bk-switch__text">
        <strong>Check pictures too {on ? 'on' : 'off'}</strong> — fetches every pinned picture and looks for faces
        under text and broken links. Slower. Off: only the plan&apos;s rules are checked.
      </span>
    </label>
  );
}

/** Claude's fixed scenes (a JSON list) go back into the last checked plan by scene_id → next plan version. */
function PasteFixedScenes({ id, onSaved }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = () => {
    setBusy(true);
    api.post(`/visual-engine/videos/${id}/scenes`, { scenes: text.trim() })
      .then((r) => { setText(''); onSaved(r.data.video); if (r.data.video?.plan?.ok) toast.success('Fixed scenes merged — the plan passed.'); })
      .catch((e) => toast.error(errorMessage(e, 'Could not merge the scenes.')))
      .finally(() => setBusy(false));
  };
  return (
    <div className="docs-paste docs-paste--scenes">
      <textarea className="docs-textarea" rows={4} value={text} spellCheck={false}
                placeholder={'Paste the list of fixed scenes Claude returned — it starts with ['}
                onChange={(e) => setText(e.target.value)} />
      <div className="docs-paste__bar">
        <button className="docs-btn" disabled={!text.trim() || busy} onClick={send}>
          {busy ? 'Merging…' : 'Merge fixed scenes and check again'}
        </button>
        {text && <span className="docs-muted">{kb(text.length)}</span>}
      </div>
    </div>
  );
}

/** The inspector's report on the finished video: measured problems per scene, with a paste for Claude. */
function Inspection({ id, report }) {
  const [rep, setRep] = useState(report || null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setRep(report || null); }, [report]);
  const fresh = () => {
    setBusy(true);
    api.get(`/visual-engine/videos/${id}/inspection?fresh=1`)
      .then((r) => setRep(r.data.report || r.data))
      .catch((e) => toast.error(errorMessage(e, 'Could not inspect the video.')))
      .finally(() => setBusy(false));
  };
  const copy = async () => {
    try {
      const r = await api.get(`/visual-engine/videos/${id}/inspection`);
      if (await copyText(r.data.text || '')) { setCopied(true); setTimeout(() => setCopied(false), 1800); }
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const problems = rep?.problems || [];
  return (
    <section className="docs-card docs-inspect">
      <div className="docs-inspect__head">
        <h2>Inspection {rep ? (rep.clean ? <span className="docs-ok">✓ clean</span> : <span className="docs-bad">{problems.length} problem{problems.length === 1 ? '' : 's'}</span>) : null}</h2>
        <div>
          {rep && !rep.clean && <button className="docs-btn docs-btn--small" onClick={copy}>{copied ? '✓ Copied' : 'Copy for Claude'}</button>}
          <button className="docs-btn docs-btn--small docs-btn--ghost" disabled={busy} onClick={fresh}>{busy ? 'Measuring…' : rep ? 'Measure again' : 'Inspect this video'}</button>
        </div>
      </div>
      {rep && rep.clean && <p className="docs-hint">Every scene was measured after the render: text whole and readable, nothing in the presenter’s box, no face covered, every graphic finished before its cut.</p>}
      {problems.length > 0 && (
        <ul className="docs-inspect__list">
          {problems.map((p, i) => (
            <li key={i}><span className="docs-fixes__where">{p.clock} · {p.scene}</span> <code>{p.tag}</code> {p.what}</li>
          ))}
        </ul>
      )}
      {!rep && <p className="docs-hint">Measures the finished video scene by scene and lists what is left to fix.</p>}
    </section>
  );
}

function PastePlan({ text, setText, checking, onCheck }) {
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) f.text().then((t) => { setText(t); onCheck(t); });
    e.target.value = '';
  };

  return (
    <div className="docs-paste">
      <textarea className="docs-textarea docs-textarea--plan" value={text} spellCheck={false}
                placeholder={'Paste the JSON Claude returned here — it starts with {'}
                onChange={(e) => setText(e.target.value)} />
      <div className="docs-paste__bar">
        <button className="docs-btn" disabled={!text.trim() || checking} onClick={() => onCheck()}>
          {checking ? 'Checking…' : 'Check plan'}
        </button>
        <button className="docs-link" onClick={() => fileRef.current?.click()}>or upload a .json file</button>
        <input ref={fileRef} type="file" accept=".json,application/json,text/plain" hidden onChange={onFile} />
        {text && <span className="docs-muted">{kb(text.length)}</span>}
      </div>
    </div>
  );
}

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};
const mmss = (sec) => (sec ? `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}` : '');

function RenderHistory({ items, playing, onPlay }) {
  if (!items || items.length < 2) return null;
  const list = [...items].reverse();
  return (
    <section className="docs-card docs-history">
      <h2>All renders <span className="docs-muted">({items.length})</span></h2>
      <p className="docs-hint">Click one to watch it. The newest is at the top.</p>
      <ul>
        {list.map((h) => {
          const on = playing === h.n;
          const what = h.kind === 'edit'
                        ? (() => {
                const ch = Array.isArray(h.changed) ? h.changed
                  : (h.changed ? String(h.changed).split('; ').filter(Boolean) : []);
                return ch.length ? `Timeline edit: ${ch.slice(0, 2).join('; ')}${ch.length > 2 ? '…' : ''}` : 'Timeline edit';
              })()
            : `Plan version ${h.plan_version ?? '?'}`;
          const b = h.brand || {};
          return (
            <li key={h.n}>
              <button type="button" className={`docs-history__row ${on ? 'is-on' : ''}`}
                      disabled={!h.video_url} onClick={() => onPlay(h.n)} aria-pressed={on}>
                {h.thumbnail_url
                  ? <img src={mediaUrl(h.thumbnail_url)} alt="" />
                  : <span className="docs-history__nothumb">#{h.n}</span>}
                <span className="docs-history__text">
                  <strong>Render {h.n}{h.n === items[items.length - 1].n ? ' · latest' : ''}</strong>
                  <span>{what}</span>
                  {(b.look || b.accent || b.background) && (
                    <span className="docs-history__brand">
                      {b.look ? `${b.look} look` : 'brand'}
                      {[b.accent, b.background, b.card].filter(Boolean).map((c) => (
                        <span key={c} className="docs-colour__sw docs-colour__sw--small" style={{ background: c }} title={c} />
                      ))}
                    </span>
                  )}
                  <span className="docs-muted">{[when(h.finished_at), mmss(h.duration_sec)].filter(Boolean).join(' · ')}</span>
                  {h.same_plan && (
                    <span className="docs-history__warn">Same plan as the render before — nothing new was pasted.</span>
                  )}
                </span>
                {on && <span className="docs-history__now">Playing</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Optional talking avatar (DreamFace): download the narration parts, upload them to DreamFace, paste the links. */
function AvatarLinks({ video, onSaved }) {
  const [parts, setParts] = useState(null);
  const [error, setError] = useState('');
  const [text, setText] = useState((video.avatar || []).join('\n'));
  const [saving, setSaving] = useState(false);
  const [getting, setGetting] = useState(null);
  const saved = (video.avatar || []).join('\n');

  useEffect(() => {
    api.get(`/visual-engine/videos/${video.id}/narration`)
      .then((r) => setParts(r.data.parts || []))
      .catch((e) => setError(errorMessage(e, 'Could not load the narration parts.')));
  }, [video.id]);

  const links = text.split(/\s+/).map((x) => x.trim()).filter(Boolean);
  const bad = links.filter((x) => !/^https?:\/\//i.test(x));
  const save = () => {
    setSaving(true);
    api.post(`/visual-engine/videos/${video.id}/avatar`, { links })
      .then((r) => { toast.success('Avatar links saved — the next render locks them to the voice.'); onSaved(r.data.video); })
      .catch((e) => toast.error(errorMessage(e, 'Could not save the links.')))
      .finally(() => setSaving(false));
  };
  const download = (p) => {
    setGetting(p.part);
    downloadBlob(`/visual-engine/videos/${video.id}/narration?part=${p.part}`,
      `${video.title || 'narration'} - part ${p.part + 1}.mp3`)
      .catch((e) => toast.error(errorMessage(e)))
      .finally(() => setGetting(null));
  };

  return (
    <div className="docs-avatar">
      <ol className="docs-avatar__steps">
        <li>
          <strong>Download the narration</strong>
          {error && <div className="docs-alert docs-alert--bad">{error}</div>}
          {!error && !parts && <span className="docs-muted"> Loading…</span>}
          {parts && (
            <div className="docs-avatar__parts">
              {parts.map((p) => (
                <button key={p.part} className="docs-btn docs-btn--small" disabled={getting !== null}
                        onClick={() => download(p)}>
                  {getting === p.part ? 'Preparing…' : `${parts.length > 1 ? `Part ${p.part + 1}` : 'Narration'} · ${mmss(p.seconds)}`}
                </button>
              ))}
            </div>
          )}
          {parts && parts.length > 1 && (
            <p className="docs-muted">DreamFace takes up to 9 minutes per video, so the narration is cut into
              {' '}{parts.length} parts at pauses. Make one avatar video per part.</p>
          )}
        </li>
        <li>
          <strong>Make the avatar in DreamFace Studio</strong>
          <span className="docs-muted">Upload {parts && parts.length > 1 ? 'each part' : 'the narration'} and copy the
            share link{parts && parts.length > 1 ? 's' : ''}.</span>
        </li>
        <li>
          <strong>Paste the link{parts && parts.length > 1 ? 's, in order' : ''}</strong>
          <textarea className="docs-textarea" rows={Math.max(2, (parts?.length || 1) + 1)} value={text}
                    placeholder="https://www.dreamfaceapp.com/creation?previewId=…"
                    onChange={(e) => setText(e.target.value)} />
          {bad.length > 0 && <div className="docs-alert docs-alert--bad">Not a link: {bad[0]}</div>}
          {parts && links.length > 0 && links.length !== parts.length && (
            <div className="docs-alert">{parts.length} part{parts.length > 1 ? 's' : ''}, {links.length} link
              {links.length > 1 ? 's' : ''} — one link per part.</div>
          )}
          <div className="docs-avatar__save">
            <button className="docs-btn" disabled={saving || !links.length || bad.length > 0 || text.trim() === saved}
                    onClick={save}>{saving ? 'Saving…' : 'Save links'}</button>
            <span className="docs-muted">
              {saved ? `${(video.avatar || []).length} link${(video.avatar || []).length > 1 ? 's' : ''} saved.`
                : 'Optional — without links the video renders with no avatar.'}
            </span>
          </div>
        </li>
      </ol>
    </div>
  );
}

/** The original script, always at hand (owner, 2026-11-02: "where we can always see our original script"):
 *  at every step — while the voice is made, while planning, after the render — folded to a few lines, opened in
 *  full on a click, with a Copy button. */
function ScriptPanel({ id }) {
  const [script, setScript] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/visual-engine/videos/${id}/script`)
      .then((r) => setScript(r.data || { script: '' }))
      .catch((e) => setError(errorMessage(e, 'Could not load the script.')));
  }, [id]);

  const copy = async () => {
    if (await copyText(script?.script || '')) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error('Copy failed — select the text and copy it instead.');
    }
  };

  if (error) return <div className="docs-alert docs-alert--bad">{error}</div>;
  if (!script) return null;
  if (!script.script) return null;
  const minutes = Math.max(1, Math.round((script.words || 0) / 150));
  return (
    <section className="docs-card docs-script">
      <div className="docs-script__head">
        <h2>Your script</h2>
        <span className="docs-muted">{(script.words || 0).toLocaleString()} words · about {minutes} min read aloud</span>
        <div className="docs-script__actions">
          <button className="docs-btn docs-btn--small" onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
          <button className="docs-btn docs-btn--small" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? 'Show less' : 'Show all'}
          </button>
        </div>
      </div>
      <div className={`docs-script__text${open ? ' docs-script__text--open' : ''}`}>{script.script}</div>
    </section>
  );
}

function Wrap({ folded, open, onToggle, children }) {
  if (!folded) return children;
  return (
    <details className="docs-more" open={open} onToggle={(e) => onToggle?.(e.currentTarget.open)}>
      <summary>Improve &amp; render again</summary>
      <div className="docs-more__body">{children}</div>
    </details>
  );
}

function DocumentaryProject() {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [error, setError] = useState('');
  const [rendering, setRendering] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [text, setText] = useState('');
  const [checking, setChecking] = useState(false);
  const [refused, setRefused] = useState('');
  const [playing, setPlaying] = useState(null);
  const [goBack, setGoBack] = useState(null); // 'brand' | 'plan' | null: the step opened from the steps bar
  const planRef = useRef(null);

  const load = useCallback(() => api.get(`/visual-engine/videos/${id}`)
    .then((r) => { setVideo(r.data.video); setError(''); })
    .catch((e) => setError(errorMessage(e, 'Could not load this documentary.'))), [id]);

  useEffect(() => { load(); }, [load]);
  // fetched ahead, so "Brand kit" opens at once when you go back to it
  useEffect(() => { loadDocOptions().catch(() => {}); loadDocChannels().catch(() => {}); }, []);
  useEffect(() => {
    if (!isBusy(video) && video?.plan?.deep?.status !== 'checking') return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [video, load]);

  const act = (path, okMsg) => api.post(`/visual-engine/videos/${id}/${path}`)
    .then((r) => { if (r.data.video) setVideo(r.data.video); else load(); if (okMsg) toast.success(okMsg); })
    .catch((e) => toast.error(errorMessage(e)));

  // Check the pasted text → the saved plan. Resolves to the updated video (or null on error).
  // Save the pasted text as the next plan version. The engine answers at once; the rules and the pictures are
  // checked in the background (the result box shows the progress and updates by itself).
  // thenRender: the render starts by itself the moment the check passes ("Check and render").
  const checkPlan = (value, thenRender = false) => {
    const plan = (value ?? text).trim();
    if (!plan) return Promise.resolve(null);
    setChecking(true);
    return api.post(`/visual-engine/videos/${id}/plan`, { plan, render_after: thenRender })
      .then((r) => {
        const v = r.data.video;
        setVideo(v);
        if (v.plan?.deep?.status === 'checking') {
          setText('');
          toast.info(thenRender ? 'Plan saved — checking it now; the render starts by itself when it passes.'
            : 'Plan saved — checking it now.');
        }
        return v;
      })
      .catch((e) => { toast.error(errorMessage(e)); return null; })
      .finally(() => setChecking(false));
  };

  const startRender = () => api.post(`/visual-engine/videos/${id}/render`)
    .then((r) => { setRefused(''); setPlaying(null); if (r.data.video) setVideo(r.data.video); else load(); toast.success('Rendering started.'); })
    .catch((e) => setRefused(errorMessage(e, 'The render could not start.')));

  // If the box holds a paste that was not checked yet, save it and let the engine render it when its check passes.
  const render = async () => {
    setRendering(true);
    try {
      if (text.trim()) {
        setRefused('');
        await checkPlan(undefined, true);
        return;
      }
      await startRender();
    } finally {
      setRendering(false);
    }
  };

  if (error && !video) {
    return (
      <div className="docs-page"><Header />
        <main className="docs-main docs-main--narrow">
          <Link to="/documentaries" className="docs-back">← Documentaries</Link>
          <div className="docs-alert docs-alert--bad">{error}</div>
        </main>
      </div>
    );
  }
  if (!video) return <div className="docs-page"><Header /><main className="docs-main"><div className="docs-empty">Loading…</div></main></div>;

  const step = video.step;
  // what can still change: from the backend, else worked out here (an older backend without can_change)
  const canChange = video.can_change || (() => {
    const open = Boolean(video.package) && ['awaiting_plan', 'completed', 'failed', 'canceled'].includes(video.status);
    return { brand: open, plan: open, render: open, voice: false };
  })();
  const avatarOn = Boolean(video.request?.brand?.avatar || video.request?.avatar || (video.avatar || []).length);
  const res = video.result || {};
  const plan = video.plan;
  const kit = video.request?.brand || {};
  const history = video.render_history || [];
  const latest = history[history.length - 1] || null;
  const latestN = latest ? latest.n : null;
  const shown = playing != null ? history.find((h) => h.n === playing && h.video_url) || null : null;
  const playSrc = shown ? shown.video_url : res.video_url;
  const playThumb = shown ? shown.thumbnail_url : res.thumbnail_url;
  const unchecked = !!text.trim();
  const canRender = unchecked || !!plan?.ok;

  return (
    <div className="docs-page">
      <Header />
      <main className="docs-main docs-main--narrow">
        <Link to="/documentaries" className="docs-back">← Documentaries</Link>
        <div className="docs-head">
          <div>
            <h1>{video.title}</h1>
            <p>
              {kit.channel_name || 'No channel'}
              {kit.voice ? `, narrated by ${kit.voice_name || kit.voice.replace(/^speechify:/, '')}` : ''}{kit.look ? `, ${kit.look} look` : ''}
              {video.package ? ` · ${Math.round(video.package.duration_sec / 60 * 10) / 10} min, ${video.package.beats} beats` : ''}
            </p>
            <VideoColours kit={kit} />
          </div>
          {isBusy(video) && (
            <button className="docs-btn docs-btn--ghost" onClick={() => setConfirmCancel(true)}>Cancel</button>
          )}
        </div>

        <ScriptPanel id={video.id} />

        {canChange.brand
          ? <GoBackSteps video={video} open={goBack} setOpen={(k) => {
            setGoBack(k);
            if (k === 'plan') setTimeout(() => planRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
          }} />
          : <Stepper step={step} />}

        {goBack === 'brand' && canChange.brand && (
          <ChangeBrand video={video} onClose={() => setGoBack(null)}
                       onSaved={(v) => { setVideo(v); setGoBack('plan'); setTimeout(() => planRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }} />
        )}

        {step === 'voice' && <Progress video={video} label="Making the voice and timings" />}

        {(step === 'voice_failed' || step === 'canceled') && (
          <div className="docs-card">
            <div className="docs-alert docs-alert--bad">{video.error?.message || 'This step did not finish.'}</div>
            <button className="docs-btn docs-btn--primary" onClick={() => act('retry', 'Trying again.')}>
              Try again
            </button>
          </div>
        )}

        {step === 'rendering' && <Progress video={video} label="Rendering your video" />}

        {step === 'render_failed' && (
          <div className="docs-alert docs-alert--bad">{video.error?.message || 'The render did not finish.'}</div>
        )}

        {step === 'done' && res.video_url && (
          <section className="docs-card docs-done">
            {shown && shown.n !== latestN && (
              <div className="docs-alert docs-alert--warn">
                You are watching render {shown.n}, an older version.{' '}
                <button type="button" className="docs-link" onClick={() => setPlaying(null)}>Back to the latest</button>
              </div>
            )}
            <video key={playSrc} className="docs-player" src={mediaUrl(playSrc)} poster={mediaUrl(playThumb)}
                   controls preload="metadata" />
            <div className="docs-actions docs-actions--left">
              <a className="docs-btn docs-btn--primary" href={mediaUrl(playSrc)} target="_blank"
                 rel="noreferrer" download>
                {shown && shown.n !== latestN ? `Download render ${shown.n}` : 'Download video'}
              </a>
              {res.has_timeline && (
                <Link className="docs-btn" to={`/documentaries/${video.id}/timeline`}>Open timeline</Link>
              )}
              <span className="docs-muted">
                {mmss(shown ? shown.duration_sec : res.duration_sec)}
                {latest ? ` · render ${(shown || latest).n}` : ''}
                {(shown || latest)?.kind === 'plan' && (shown || latest).plan_version ? `, plan version ${(shown || latest).plan_version}` : ''}
              </span>
            </div>
          </section>
        )}

        {step === 'done' && res.has_timeline && <Inspection id={video.id} report={res.inspection} />}

        {step === 'done' && <RenderHistory items={history} playing={shown ? shown.n : latestN} onPlay={(n) => { setPlaying(n === latestN ? null : n); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />}

        {(step === 'plan' || step === 'done' || step === 'render_failed') && (
          <>
          <div ref={planRef} />
          <Wrap folded={step === 'done'} open={goBack === 'plan'} onToggle={(o) => setGoBack(o ? 'plan' : (goBack === 'plan' ? null : goBack))}>
            <section className="docs-card">
              <h2><span className="docs-num">A</span> Copy the timings to Claude</h2>
              <p className="docs-hint">
                Open a new chat in Claude, paste everything (or attach the zip) and send. Claude replies with one
                JSON plan.
              </p>
              <CopyForClaude id={video.id} title={video.title} />
            </section>

            <section className="docs-card">
              <h2><span className="docs-num">B</span> Paste Claude&apos;s plan</h2>
              {step === 'done' && (
                <p className="docs-hint">Ask Claude (in the same chat) for the changes you want, paste the new plan
                  here and render again. The current video stays until the new one is ready.</p>
              )}
              <PastePlan text={text} setText={setText} checking={checking} onCheck={checkPlan} />
              <PictureCheck video={video} onSaved={(v) => { if (v) setVideo(v); else load(); }} />
              <PlanResult id={video.id} plan={plan} />
              {plan && !plan.ok && plan.deep?.status !== 'checking' && (
                <div className="docs-scenes">
                  <h3>Paste fixed scenes</h3>
                  <PasteFixedScenes id={video.id} onSaved={(v) => { if (v) setVideo(v); else load(); }} />
                </div>
              )}
            </section>

            {avatarOn && (
              <section className="docs-card">
                <h2><span className="docs-num">C</span> Talking avatar <span className="docs-optional">optional</span></h2>
                <p className="docs-hint">This channel uses a DreamFace presenter. The engine locks it to the voice,
                  so its lips always match wherever Claude shows it.</p>
                <AvatarLinks video={video} onSaved={setVideo} />
              </section>
            )}

            {refused && <div className="docs-alert docs-alert--bad">{refused}</div>}
            <div className="docs-actions docs-actions--col">
              <button className="docs-btn docs-btn--primary docs-btn--big" disabled={!canRender || rendering || checking}
                      onClick={render}>
                {rendering ? (checking ? 'Checking…' : 'Starting…')
                  : unchecked ? 'Check and render'
                    : step === 'plan' ? 'Render video' : 'Render again'}
              </button>
              <span className="docs-muted docs-render-note">
                {unchecked
                  ? 'The new plan in the box will be checked first, then rendered.'
                  : plan?.ok
                    ? `Will render plan version ${plan.version}${latest?.kind === 'plan' && latest.plan_version === plan.version ? ' — the same plan as the last render' : ''}.`
                    : plan?.deep?.status === 'checking'
                      ? (plan.render_after ? 'Checking — the render starts by itself when the check passes.'
                        : 'Checking — the render opens when that is done.')
                      : plan ? 'Fix the problems above (paste the fixed scenes), or paste a new plan.' : 'Paste Claude\'s plan to render.'}
              </span>
            </div>
          </Wrap>
          </>
        )}
      </main>

      {confirmCancel && (
        <ConfirmationBox
          message={step === 'voice' ? 'Cancel making the voice?' : 'Stop this render?'}
          handleConfirm={(yes) => { setConfirmCancel(false); if (yes) act('cancel', 'Canceled.'); }}
        />
      )}
    </div>
  );
}

export default DocumentaryProject;
